// Minimal store-only (no-compression) ZIP writer.
//
// The Phase-4 export path bundles multi-file artifacts (one MPR per
// cabinet, one DXF per panel) into a single download. Real ZIP compression
// is not worth a third-party dependency at this size: total content is a
// few hundred KB of ASCII, and `Content-Encoding: gzip` on the HTTP
// download would already squash it if we ever serve through the WebServer.
//
// Format reference: PKWARE APPNOTE.TXT 6.3.10
//   - Local file header (per file): 30 bytes + filename
//   - File data: raw bytes (CompressionMethod = 0 = "Stored")
//   - Central directory file header (per file): 46 bytes + filename
//   - End of central directory record: 22 bytes
//
// Compatible with browser unzip, Windows Explorer, `unzip(1)`, JSZip.

const SIGNATURE_LOCAL    = 0x04034b50;
const SIGNATURE_CENTRAL  = 0x02014b50;
const SIGNATURE_END      = 0x06054b50;

interface ZipFile {
  name: string;
  data: Uint8Array;
  crc32: number;
}

export interface ZipInput {
  name: string;
  content: string | Uint8Array;
}

/** Build a ZIP blob (mimeType "application/zip") from a list of files. */
export function makeZip(files: ZipInput[]): Uint8Array {
  const enc = new TextEncoder();
  const records: ZipFile[] = files.map((f) => {
    const data = typeof f.content === "string" ? enc.encode(f.content) : f.content;
    return { name: f.name, data, crc32: crc32(data) };
  });

  // Compute total size.
  let localSize = 0;
  let centralSize = 0;
  for (const r of records) {
    localSize   += 30 + r.name.length + r.data.length;
    centralSize += 46 + r.name.length;
  }

  const total = localSize + centralSize + 22;
  const buf = new Uint8Array(total);
  const dv  = new DataView(buf.buffer);
  let off = 0;
  const offsets: number[] = [];

  // Local file headers + data
  for (const r of records) {
    offsets.push(off);
    dv.setUint32(off + 0, SIGNATURE_LOCAL, true);
    dv.setUint16(off + 4, 20, true);                // version needed
    dv.setUint16(off + 6, 0, true);                 // flags
    dv.setUint16(off + 8, 0, true);                 // method: 0 = Stored
    dv.setUint16(off + 10, 0, true);                // mod time
    dv.setUint16(off + 12, 0, true);                // mod date
    dv.setUint32(off + 14, r.crc32, true);          // CRC-32
    dv.setUint32(off + 18, r.data.length, true);    // compressed size
    dv.setUint32(off + 22, r.data.length, true);    // uncompressed size
    dv.setUint16(off + 26, r.name.length, true);    // filename length
    dv.setUint16(off + 28, 0, true);                // extra length
    off += 30;
    buf.set(enc.encode(r.name), off); off += r.name.length;
    buf.set(r.data, off); off += r.data.length;
  }

  // Central directory
  const cdStart = off;
  for (let i = 0; i < records.length; i++) {
    const r = records[i]!;
    dv.setUint32(off + 0, SIGNATURE_CENTRAL, true);
    dv.setUint16(off + 4, 20, true);                // version made by
    dv.setUint16(off + 6, 20, true);                // version needed
    dv.setUint16(off + 8, 0, true);                 // flags
    dv.setUint16(off + 10, 0, true);                // method
    dv.setUint16(off + 12, 0, true);                // mod time
    dv.setUint16(off + 14, 0, true);                // mod date
    dv.setUint32(off + 16, r.crc32, true);
    dv.setUint32(off + 20, r.data.length, true);    // compressed
    dv.setUint32(off + 24, r.data.length, true);    // uncompressed
    dv.setUint16(off + 28, r.name.length, true);
    dv.setUint16(off + 30, 0, true);                // extra
    dv.setUint16(off + 32, 0, true);                // comment
    dv.setUint16(off + 34, 0, true);                // disk #
    dv.setUint16(off + 36, 0, true);                // internal attrs
    dv.setUint32(off + 38, 0, true);                // external attrs
    dv.setUint32(off + 42, offsets[i]!, true);      // local header offset
    off += 46;
    buf.set(enc.encode(r.name), off); off += r.name.length;
  }
  const cdSize = off - cdStart;

  // End of central directory
  dv.setUint32(off + 0, SIGNATURE_END, true);
  dv.setUint16(off + 4, 0, true);                   // disk #
  dv.setUint16(off + 6, 0, true);                   // start disk
  dv.setUint16(off + 8, records.length, true);      // records on this disk
  dv.setUint16(off + 10, records.length, true);     // total records
  dv.setUint32(off + 12, cdSize, true);
  dv.setUint32(off + 16, cdStart, true);
  dv.setUint16(off + 20, 0, true);                  // comment length

  return buf;
}

// ── CRC-32 (IEEE 802.3 polynomial) ───────────────────────────────

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    t[n] = c;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}
